import { GrundVorlagenPicker } from "../../components/GrundVorlagen";

import { api } from "../../lib/api";
import { CodeGate } from "../../components/CodeGate";
import { Checkbox, Dl, Field, Input, Select, Textarea, Button } from "../../components/kd";
import { Sparkles } from "lucide-react";
import { KiCtx, KiPreview, MicButton, useKi, useKiText } from "../../components/Ki";
import { ToothChart } from "../../components/ToothChart";
import { FileUploader } from "../../components/FileUploader";
import { UrgencyBadge } from "../../components/Badges";
import { DATEITYP, DRINGLICHKEIT, GRUND, VERSICHERUNG, WUNSCH, fmtDate, fmtSize, maskDateDE, parseGermanDate } from "../../lib/format";
import { CheckCircle2 } from "lucide-react";

export const EMPTY = {
  ueberweiser: { email: "", praxisname: "", zahnarzt_name: "", telefon: "", strasse: "", plz: "", ort: "" },
  patient_vorname: "", patient_nachname: "", geburtsdatum_text: "", patient_geburtsdatum: "", patient_telefon: "", patient_email: "", versicherung: "",
  grund: "", grund_freitext: "", zaehne: [] as number[], dringlichkeit: "NORMAL", behandlungswunsch: "", bemerkung: "",
  einwilligung_patient: false, hinweis_arztwahl: false, testdaten_bestaetigt: false, datei_ids: [] as string[], dateien: [] as any[],
};

const req = (v: string) => !String(v || "").trim();

export function validateStep(step: number, d: any, verified: string | null, testmodus = false): Record<string, string> {
  const e: Record<string, string> = {};
  const u = d.ueberweiser;
  if (step === 0) {
    if (!verified) e.email = "Bitte bestätigen Sie zuerst Ihre E-Mail-Adresse.";
    if (req(u.praxisname)) e.praxisname = "Bitte geben Sie den Praxisnamen an.";
    if (req(u.zahnarzt_name)) e.zahnarzt_name = "Bitte geben Sie den Namen des Zahnarztes an.";
    if (req(u.telefon)) e.telefon = "Bitte geben Sie eine Telefonnummer an.";
  }
  if (step === 1) {
    if (req(d.patient_vorname)) e.patient_vorname = "Bitte geben Sie den Vornamen an.";
    if (req(d.patient_nachname)) e.patient_nachname = "Bitte geben Sie den Nachnamen an.";
    const iso = parseGermanDate(d.geburtsdatum_text || "");
    if (!iso) e.geburtsdatum = /^\d{2}\.\d{2}\.\d{4}$/.test(d.geburtsdatum_text || "") ? "Dieses Datum existiert nicht. Bitte prüfen Sie Tag und Monat." : "Bitte geben Sie das Geburtsdatum im Format TT.MM.JJJJ an (Jahr vierstellig).";
    else {
      const dt = new Date(iso + "T00:00:00");
      const min = new Date(); min.setFullYear(min.getFullYear() - 120);
      if (dt > new Date()) e.geburtsdatum = "Das Geburtsdatum darf nicht in der Zukunft liegen.";
      else if (dt < min) e.geburtsdatum = "Das Geburtsdatum darf nicht mehr als 120 Jahre zurückliegen.";
    }
    if (req(d.patient_telefon)) e.patient_telefon = "Bitte geben Sie eine Telefonnummer an.";
    if (d.patient_email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(d.patient_email)) e.patient_email = "Bitte geben Sie eine gültige E-Mail-Adresse an.";
    if (req(d.versicherung)) e.versicherung = "Bitte wählen Sie die Versicherung.";
  }
  if (step === 2) {
    if (req(d.grund)) e.grund = "Bitte wählen Sie einen Überweisungsgrund.";
    if (d.grund === "SONSTIGES" && req(d.grund_freitext)) e.grund_freitext = "Bitte beschreiben Sie den Grund.";
    if (req(d.behandlungswunsch)) e.behandlungswunsch = "Bitte wählen Sie einen Behandlungswunsch.";
  }
  if (step === 4) {
    if (!d.einwilligung_patient) e.einwilligung_patient = "Pflichtangabe";
    if (!d.hinweis_arztwahl) e.hinweis_arztwahl = "Pflichtangabe";
    if (testmodus && !d.testdaten_bestaetigt) e.testdaten_bestaetigt = "Pflichtangabe";
  }
  return e;
}

type P = { data: any; set: (p: any) => void; errors: Record<string, string>; slug: string; verified: string | null; setVerified: (e: string) => void; setUploading: (b: boolean) => void; testmodus?: boolean };

function TextField({ label, name, value, onChange, error, required, type = "text", testid, ...rest }: any) {
  return (
    <Field label={label} error={error} required={required} htmlFor={name}>
      <Input id={name} type={type} value={value} onChange={(e) => onChange(e.target.value)} aria-invalid={!!error} data-testid={testid} {...rest} />
    </Field>
  );
}

export function StepPraxis({ data, set, errors, slug, verified, setVerified }: P) {
  const u = data.ueberweiser;
  const su = (k: string) => (v: string) => set({ ueberweiser: { ...u, [k]: v } });
  if (!verified) {
    return (
      <>
        <CodeGate
          title="Ihre Praxis"
          intro="Bitte bestätigen Sie zunächst die E-Mail-Adresse Ihrer Praxis. Wir senden Ihnen einen 6-stelligen Code (10 Minuten gültig)."
          onRequest={(email) => api.post(`/public/praxis/${slug}/code`, { email })}
          onVerify={async (email, code) => {
            const r = await api.post(`/public/praxis/${slug}/code/pruefen`, { email, code });
            setVerified(r.data.email);
            set({ ueberweiser: { ...u, ...(r.data.ueberweiser || {}), email: r.data.email } });
          }}
        />
      </>
    );
  }
  return (
    <>
      <h2 className="mb-1">Ihre Praxis</h2>
      <p className="mb-5 flex items-center gap-1.5 text-sm text-success" data-testid="email-verified"><CheckCircle2 className="h-4 w-4" aria-hidden />E-Mail bestätigt: {verified}</p>
      <TextField label="Praxisname" name="praxisname" required value={u.praxisname} onChange={su("praxisname")} error={errors.praxisname} testid="ueb-praxisname-input" autoComplete="organization" />
      <TextField label="Name des Zahnarztes" name="zahnarzt_name" required value={u.zahnarzt_name} onChange={su("zahnarzt_name")} error={errors.zahnarzt_name} testid="ueb-zahnarzt-input" />
      <TextField label="Telefon" name="telefon" type="tel" required value={u.telefon} onChange={su("telefon")} error={errors.telefon} testid="ueb-telefon-input" autoComplete="tel" />
      <TextField label="Straße und Hausnummer" name="strasse" value={u.strasse} onChange={su("strasse")} testid="ueb-strasse-input" />
      <div className="grid grid-cols-3 gap-3">
        <TextField label="PLZ" name="plz" value={u.plz} onChange={su("plz")} testid="ueb-plz-input" inputMode="numeric" />
        <div className="col-span-2"><TextField label="Ort" name="ort" value={u.ort} onChange={su("ort")} testid="ueb-ort-input" /></div>
      </div>
    </>
  );
}

export function StepPatient({ data, set, errors }: P) {
  const s = (k: string) => (v: string) => set({ [k]: v });
  return (
    <>
      <h2 className="mb-5">Patient</h2>
      <div className="grid gap-x-3 sm:grid-cols-2">
        <TextField label="Vorname" name="pv" required value={data.patient_vorname} onChange={s("patient_vorname")} error={errors.patient_vorname} testid="patient-vorname-input" />
        <TextField label="Nachname" name="pn" required value={data.patient_nachname} onChange={s("patient_nachname")} error={errors.patient_nachname} testid="patient-nachname-input" />
      </div>
      <TextField label="Geburtsdatum (TT.MM.JJJJ)" name="geb" required placeholder="TT.MM.JJJJ" inputMode="numeric" maxLength={10} value={data.geburtsdatum_text}
        onChange={(v: string) => { const t = maskDateDE(v, data.geburtsdatum_text); set({ geburtsdatum_text: t, patient_geburtsdatum: parseGermanDate(t) || "" }); }} error={errors.geburtsdatum} testid="patient-geburtsdatum-input" />
      <TextField label="Telefon" name="pt" type="tel" required value={data.patient_telefon} onChange={s("patient_telefon")} error={errors.patient_telefon} testid="patient-telefon-input" />
      <TextField label="E-Mail (optional)" name="pe" type="email" value={data.patient_email} onChange={s("patient_email")} error={errors.patient_email} testid="patient-email-input" />
      <Field label="Versicherung" required error={errors.versicherung} htmlFor="vers">
        <Select id="vers" options={VERSICHERUNG} placeholder="Bitte wählen …" value={data.versicherung} onChange={(e) => set({ versicherung: e.target.value })} data-testid="patient-versicherung-select" />
      </Field>
    </>
  );
}

function Radios({ name, options, value, onChange, testid }: { name: string; options: Record<string, string>; value: string; onChange: (v: string) => void; testid: string }) {
  return (
    <div className="grid gap-2 sm:grid-cols-3" role="radiogroup">
      {Object.entries(options).map(([k, l]) => (
        <label key={k} className={`flex min-h-[44px] cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 transition-colors ${value === k ? "border-primary bg-primary-light" : "border-border bg-surface hover:border-text-muted"}`}>
          <input type="radio" name={name} value={k} checked={value === k} onChange={() => onChange(k)} className="h-4 w-4 accent-[var(--kd-primary)]" data-testid={`${testid}-${k}`} />
          {name === "dringlichkeit" ? <UrgencyBadge value={k} /> : <span>{l}</span>}
        </label>
      ))}
    </div>
  );
}

function ReferralKi({ data, set, slug }: { data: any; set: (p: any) => void; slug: string }) {
  const ctx: KiCtx = { slug, patient: { patient_vorname: data.patient_vorname, patient_nachname: data.patient_nachname, patient_geburtsdatum: data.patient_geburtsdatum, patient_telefon: data.patient_telefon, patient_email: data.patient_email || "" } };
  const flags = useKi({ slug });
  const kt = useKiText(ctx);
  if (!flags.sprache && !flags.assistent) return null;
  return (
    <div className="-mt-2 mb-4">
      <div className="flex flex-wrap items-center gap-2">
        {flags.sprache && <MicButton ctx={ctx} onText={(t) => set({ grund_freitext: data.grund_freitext ? `${data.grund_freitext} ${t}` : t })} testid="freitext-mic" />}
        {flags.assistent && <Button variant="secondary" loading={!!kt.busy} disabled={!data.grund_freitext?.trim()} onClick={() => kt.ask("freitext", data.grund_freitext)} data-testid="freitext-ki-button"><Sparkles className="h-4 w-4" aria-hidden />Freitext aus Stichpunkten</Button>}
      </div>
      {kt.suggestion && <KiPreview text={kt.suggestion.text} ctx={ctx} aktion="freitext" onAccept={(t) => set({ grund_freitext: t.slice(0, 2000) })} onClose={kt.clear} />}
    </div>
  );
}

function BemerkungMic({ data, set, slug }: { data: any; set: (p: any) => void; slug: string }) {
  const flags = useKi({ slug });
  if (!flags.sprache) return null;
  return <div className="-mt-2 mb-4"><MicButton ctx={{ slug }} onText={(t) => set({ bemerkung: data.bemerkung ? `${data.bemerkung} ${t}` : t })} testid="bemerkung-mic" /></div>;
}

export function StepReferral({ data, set, errors, slug }: P) {
  return (
    <>
      <h2 className="mb-5">Überweisung</h2>
      <GrundVorlagenPicker onPick={(v) => set({ ...(v.grund ? { grund: v.grund } : {}), grund_freitext: (data.grund_freitext ? `${data.grund_freitext}\n${v.text}` : v.text).slice(0, 2000) })} />
      <Field label="Überweisungsgrund" required error={errors.grund} htmlFor="grund">
        <Select id="grund" options={GRUND} placeholder="Bitte wählen …" value={data.grund} onChange={(e) => set({ grund: e.target.value })} data-testid="grund-select" />
      </Field>
      <Field label={`Beschreibung${data.grund === "SONSTIGES" ? "" : " (optional)"}`} required={data.grund === "SONSTIGES"} error={errors.grund_freitext} htmlFor="frei" hint={`${(data.grund_freitext || "").length}/2000 Zeichen`}>
        <Textarea id="frei" maxLength={2000} value={data.grund_freitext} onChange={(e) => set({ grund_freitext: e.target.value })} data-testid="grund-freitext-input" />
      </Field>
      <ReferralKi data={data} set={set} slug={slug} />
      <div className="mb-4">
        <p className="kd-label">Betroffene Zähne (FDI, Mehrfachauswahl)</p>
        <ToothChart value={data.zaehne} onChange={(z) => set({ zaehne: z })} />
        <p className="mt-1 text-sm text-text-muted" data-testid="selected-teeth">Ausgewählt: {data.zaehne.length ? data.zaehne.join(", ") : "keine"}</p>
      </div>
      <Field label="Dringlichkeit" required>
        <Radios name="dringlichkeit" options={DRINGLICHKEIT} value={data.dringlichkeit} onChange={(v) => set({ dringlichkeit: v })} testid="dringlichkeit" />
      </Field>
      <Field label="Behandlungswunsch" required error={errors.behandlungswunsch}>
        <Radios name="wunsch" options={WUNSCH} value={data.behandlungswunsch} onChange={(v) => set({ behandlungswunsch: v })} testid="wunsch" />
      </Field>
      <Field label="Bemerkung (optional)" htmlFor="bem">
        <Textarea id="bem" maxLength={2000} value={data.bemerkung} onChange={(e) => set({ bemerkung: e.target.value })} data-testid="bemerkung-input" />
      </Field>
      <BemerkungMic data={data} set={set} slug={slug} />
    </>
  );
}

export function StepFiles({ data, set, slug, setUploading }: P) {
  return (
    <>
      <h2 className="mb-1">Dateien</h2>
      <p className="mb-5 text-text-muted">Röntgenbilder, Fotos oder Überweisungsschein (optional). Wählen Sie je Datei den Typ, dann startet der Upload.</p>
      <FileUploader
        uploadUrl={`/public/praxis/${slug}/dateien`}
        deleteUrl={(id) => `/public/praxis/${slug}/dateien/${id}`}
        initial={data.dateien}
        onChange={(ids, items) => {
          setUploading(items.some((i) => i.state === "laden"));
          set({ datei_ids: ids, dateien: items.filter((i) => i.state === "fertig").map((i) => ({ id: i.id, name: i.name, size: i.size, typ: i.typ })) });
        }}
      />
      {data.dateien.length > 0 && <p className="mt-3 text-sm text-text-muted">{data.dateien.length} Datei(en) hochgeladen.</p>}
    </>
  );
}

export function StepConfirm({ data, set, errors, testmodus }: P) {
  const u = data.ueberweiser;
  return (
    <>
      <h2 className="mb-5">Bestätigung</h2>
      <div className="space-y-5" data-testid="referral-summary">
        <section><h3 className="mb-2">Ihre Praxis</h3><Dl items={[["Praxis", u.praxisname], ["Zahnarzt", u.zahnarzt_name], ["E-Mail", u.email], ["Telefon", u.telefon], ["Adresse", [u.strasse, `${u.plz} ${u.ort}`.trim()].filter(Boolean).join(", ")]]} /></section>
        <section><h3 className="mb-2">Patient</h3><Dl items={[["Name", `${data.patient_vorname} ${data.patient_nachname}`], ["Geburtsdatum", fmtDate(data.patient_geburtsdatum)], ["Telefon", data.patient_telefon], ["E-Mail", data.patient_email], ["Versicherung", VERSICHERUNG[data.versicherung]]]} /></section>
        <section><h3 className="mb-2">Überweisung</h3><Dl items={[["Grund", GRUND[data.grund]], ["Beschreibung", data.grund_freitext], ["Zähne", data.zaehne.join(", ")], ["Dringlichkeit", <UrgencyBadge value={data.dringlichkeit} />], ["Behandlungswunsch", WUNSCH[data.behandlungswunsch]], ["Bemerkung", data.bemerkung]]} /></section>
        <section><h3 className="mb-2">Dateien</h3>
          {data.dateien.length ? <ul className="list-inside list-disc">{data.dateien.map((f: any, i: number) => <li key={i}>{f.name} ({DATEITYP[f.typ]}, {fmtSize(f.size)})</li>)}</ul> : <p className="text-text-muted">Keine Dateien</p>}
        </section>
      </div>
      <div className="mt-6 space-y-3">
        <Checkbox testid="consent-einwilligung" error={!!errors.einwilligung_patient} checked={data.einwilligung_patient} onChange={(v) => set({ einwilligung_patient: v })}
          label="Der Patient ist mit der Überweisung und der Übermittlung seiner Daten an die Fachpraxis einverstanden." />
        <Checkbox testid="consent-arztwahl" error={!!errors.hinweis_arztwahl} checked={data.hinweis_arztwahl} onChange={(v) => set({ hinweis_arztwahl: v })}
          label="Der Patient wurde darauf hingewiesen, dass er die behandelnde Praxis frei wählen kann." />
        {(errors.einwilligung_patient || errors.hinweis_arztwahl) && <p className="text-sm text-danger" role="alert">Bitte bestätigen Sie beide Hinweise.</p>}
        {testmodus && <Checkbox testid="consent-testdaten" error={!!errors.testdaten_bestaetigt} checked={data.testdaten_bestaetigt} onChange={(v) => set({ testdaten_bestaetigt: v })}
          label="Ich verwende ausschließlich fiktive Testdaten." />}
        {errors.testdaten_bestaetigt && <p className="text-sm text-danger" role="alert">Im Testbetrieb sind nur fiktive Testdaten erlaubt – bitte bestätigen.</p>}
      </div>
    </>
  );
}
